import Axios from "helpers/Interceptor";
import { OrganizationRole, UserPermissionOnBrand } from "modules/Const";
import { CurrentBrand, OrganizationData, OrganizationInvitationStatus, OrganizationStatus, OrganizationTeamData, OrganizationUser } from "modules/Types";

/** Interface for brand permission updates when updating organization users */
export interface OrganizationUserBrandUpdate {
    /** Brand ID */
    id: number;
    /** Permission level for this brand (NONE removes the user from the brand) */
    permissionLevel: UserPermissionOnBrand;
}

/** Response interface for organization user updates */
export interface UpdateOrganizationUserResponse {
    /** Updated list of organization users */
    organizationUsers: OrganizationUser[];
    /** Success message */
    message?: string;
}

export const getOrganization_api = async ({
    organizationId,
    brandId,
    userId,
}: {
    organizationId?: number;
    brandId?: number;
    userId?: number;
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const queryParams = new URLSearchParams();
        if (brandId) {
            queryParams.append('brandId', brandId.toString());
        }
        if (userId) {
            queryParams.append('userId', userId.toString());
        }

        const queryString = queryParams.toString();
        const url = `/v2/organization/${organizationId}${queryString ? `?${queryString}` : ''}`;

        const res = await Axios.get(url);
        return res.data;
    } catch (error) {
        return null;
    }
}

export const createOrganization_api = async ({
    name,
    emails,
    setupStatus,
    ownerId,
}: {
    name: string;
    emails: string[];
    setupStatus: OrganizationStatus;
    ownerId: number;
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post('/v2/organization', {
            name,
            emails,
            setupStatus,
            ownerId,
        });
        return res.data;
    } catch (error) {
        return null;
    }
}

export const updateOrganization_api = async ({
    organizationId,
    name,
    setupStatus,
    profile_picture,
}: {
    organizationId: number;
    name?: string;
    setupStatus?: OrganizationStatus;
    profile_picture?: string;
}): Promise<{ organization: any } | null | undefined> => {
    try {
        const res = await Axios.put(`/v2/organization/${organizationId}`, {
            name,
            setupStatus,
            profile_picture,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update organization:', error);
        return null;
    }
}

/**
 * Update organization with partial data - only updates the fields provided
 * @param organizationId - ID of the organization to update
 * @param updates - Object containing the fields to update
 * @returns Promise containing the updated organization data
 */
export const updateOrganizationPartial_api = async (
    organizationId: number,
    updates: {
        name?: string;
        profile_picture?: string;
    }
): Promise<{ organization: any } | null> => {
    try {
        const res = await Axios.put(`/v2/organization/${organizationId}`, updates);
        return res.data;
    } catch (error) {
        console.error('Failed to update organization:', error);
        return null;
    }
}

export const inviteOrganizationUser_api = async ({
    organizationId,
    users,
    brands,
}: {
    organizationId: number;
    users: {
        email: string;
        role: OrganizationRole;
        firstName: string;
        lastName: string;
    }[];
    brands: CurrentBrand[];
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization/${organizationId}/invite-users`, {
            users,
            brands,
        });
        return res.data;
    } catch (error) {
        return null;
    }
}

export const respondOrganizationUserInvite_api = async ({
    organizationId,
    userId,
    invitationStatus,
    brandIds,
}: {
    organizationId: number;
    userId: number;
    invitationStatus: OrganizationInvitationStatus;
    brandIds?: number[];
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization/respond-user-invite`, {
            organizationId,
            userId,
            invitationStatus,
            brandIds,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to respond to organization user invite:', error);
        return null;
    }
}

export const checkOrganizationUserInvitationStatus_api = async ({
    organizationId,
    userId,
}: {
    organizationId: number;
    userId: number;
}): Promise<{ invitationStatus: OrganizationInvitationStatus } | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization/check-user-invitation-status`, {
            organizationId,
            userId,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to check organization user invitation status:', error);
        return null;
    }
}

export const createOrganizationBrand_api = async ({
    organizationId,
    brandId,
}: {
    organizationId: number;
    brandId: number;
}): Promise<{ organizationBrands: any[], organizationUsers: any[] } | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization/brand/${organizationId}`, {
            brandId,
        });
        return res.data
    } catch (error) {
        console.error('Failed to create organization brand:', error);
        return null;
    }
}

export const adminAddExistingBrandToOrganization_api = async ({
    organizationId,
    brandId,
    existingUserPermissions = [],
    organizationUserPermissions = []
}: {
    organizationId: number;
    brandId: number;
    existingUserPermissions?: Array<{
        userId: number;
        permissionLevel: string;
    }>;
    organizationUserPermissions?: Array<{
        userId: number;
        permissionLevel: string;
    }>;
}): Promise<{ organizationUsers: any[], brandAdded: any } | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization/${organizationId}/admin-add-existing-brand`, {
            brandId,
            existingUserPermissions,
            organizationUserPermissions
        });
        return res.data
    } catch (error) {
        console.error('Failed to add existing brand to organization:', error);
        throw error; // Re-throw to allow proper error handling in components
    }
}

export const updateOrganizationUser_api = async ({
    organizationId,
    userId,
    role,
    brands,
}: {
    organizationId: number;
    userId: number;
    role: OrganizationRole;
    brands: OrganizationUserBrandUpdate[];
}): Promise<UpdateOrganizationUserResponse> => {
    try {
        const res = await Axios.put(`/v2/organization/user/${organizationId}/${userId}`, {
            role,
            brands,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update organization user:', error);
        throw error; // Re-throw to allow proper error handling in the hook
    }
}

export const updateUserBrand_api = async ({
    organizationId,
    brandId,
    users,
}: {
    organizationId: number;
    brandId: number;
    users: {
        userId: number;
        permissionLevel: UserPermissionOnBrand;
    }[];
}): Promise<{ organizationUsers: any[] }> => {
    try {
        const res = await Axios.put(`/v2/organization/user-brand/${organizationId}/${brandId}`, {
            users,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update user brand:', error);
        return { organizationUsers: [] };
    }
}

export const updateUserPermissionsOnBrand_api = async ({
    organizationId,
    brandId,
    updates,
}: {
    organizationId: number;
    brandId: number;
    updates: {
        userId: number;
        permissionLevel: UserPermissionOnBrand | 'none';
    }[];
}): Promise<{ organizationUsers: any[] }> => {
    try {
        const res = await Axios.put(`/v2/organization/${organizationId}/brand/${brandId}/users`, {
            updates,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update user permissions on brand:', error);
        throw error;
    }
}

export const deleteOrganizationBrand_api = async ({
    organizationId,
    brandId,
}: {
    organizationId: number;
    brandId: number;
}): Promise<{ organizationUsers: any[] } | null | undefined> => {
    try {
        const res = await Axios.delete(`/v2/organization/${organizationId}/brand/${brandId}`);
        return res.data;
    } catch (error) {
        console.error('Failed to remove organization brand:', error);
        return null;
    }
}

export const deleteOrganizationUser_api = async ({
    organizationId,
    userId,
}: {
    organizationId: number;
    userId: number;
}): Promise<{ message: string } | null> => {
    try {
        const res = await Axios.delete(`/v2/organization/${organizationId}/user/${userId}`);
        return res.data;
    } catch (error) {
        console.error('Failed to remove organization user:', error);
        return null;
    }
}

export const deleteOrganization_api = async ({
    organizationId,
}: {
    organizationId: number;
}): Promise<any | null> => {
    try {
        const res = await Axios.delete(`/v2/organization/${organizationId}`);
        return res.data;
    } catch (error) {
        console.error('Failed to delete organization:', error);
        return null;
    }
}